<template>
  <form @submit.prevent="handleSubmit" class="program-form">
    <div class="form-field">
      <IntercomInput
        v-model="formData.name"
        :label="$t('program.name')"
        placeholder="e.g., Annadanam, Pilgrimage Support"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomSelect
        v-model="formData.program_type"
        :label="$t('program.type')"
        :options="typeOptions"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-row">
      <div class="form-field">
        <IntercomInput
          v-model="formData.metric_label"
          :label="$t('program.metricLabel')"
          placeholder="e.g., Meals Served, Participants"
          required
          :disabled="loading"
        />
        <p class="field-hint">{{ $t('program.metricLabelHint') }}</p>
      </div>

      <div class="form-field">
        <IntercomInput
          v-model="formData.metric_unit"
          :label="$t('program.metricUnit')"
          placeholder="e.g., meals, people, km"
          required
          :disabled="loading"
        />
      </div>
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.description"
        :label="$t('program.description')"
        placeholder="Optional description of this program"
        :disabled="loading"
      />
    </div>

    <div v-if="!program?.is_default" class="form-field form-field--checkbox">
      <label class="checkbox-label">
        <input
          v-model="formData.is_active"
          type="checkbox"
          :disabled="loading"
          class="checkbox-input"
        />
        <span>{{ $t('program.isActive') }}</span>
      </label>
    </div>

    <div class="form-actions">
      <IntercomButton type="button" variant="ghost" @click="$emit('cancel')" :disabled="loading">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton type="submit" variant="primary" :loading="loading">
        {{ $t('common.save') }}
      </IntercomButton>
    </div>
  </form>
</template>

<script setup>
import { reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  IntercomInput,
  IntercomSelect,
  IntercomButton,
} from '@shared/components/intercom'

const { t } = useI18n()

const props = defineProps({
  program: { type: Object, default: null },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'cancel'])

const formData = reactive({
  name: '',
  program_type: 'custom',
  metric_label: 'Units',
  metric_unit: 'units',
  description: '',
  is_active: true,
})

const typeOptions = computed(() => [
  { value: 'food_distribution', label: t('program.types.food_distribution') },
  { value: 'pilgrimage', label: t('program.types.pilgrimage') },
  { value: 'healthcare', label: t('program.types.healthcare') },
  { value: 'education', label: t('program.types.education') },
  { value: 'custom', label: t('program.types.custom') },
])

// Auto-fill metric_label and metric_unit based on program type
const typeDefaults = {
  food_distribution: { label: 'Meals Served', unit: 'meals' },
  pilgrimage: { label: 'Pilgrims Served', unit: 'pilgrims' },
  healthcare: { label: 'Patients Treated', unit: 'patients' },
  education: { label: 'Students Supported', unit: 'students' },
  custom: { label: 'Units', unit: 'units' },
}

watch(() => formData.program_type, (newType) => {
  // Only auto-fill if user hasn't manually customized the values
  // (i.e., they still match a known default or the initial "Units")
  const allDefaults = Object.values(typeDefaults).map(d => d.label)
  allDefaults.push('Units')
  if (allDefaults.includes(formData.metric_label) && newType && typeDefaults[newType]) {
    formData.metric_label = typeDefaults[newType].label
    formData.metric_unit = typeDefaults[newType].unit
  }
})

watch(() => props.program, (newProgram) => {
  if (newProgram) {
    formData.name = newProgram.name || ''
    formData.program_type = newProgram.program_type || 'custom'
    formData.metric_label = newProgram.metric_label || 'Units'
    formData.metric_unit = newProgram.metric_unit || 'units'
    formData.description = newProgram.description || ''
    formData.is_active = newProgram.is_active !== false
  } else {
    formData.name = ''
    formData.program_type = 'custom'
    formData.metric_label = 'Units'
    formData.metric_unit = 'units'
    formData.description = ''
    formData.is_active = true
  }
}, { immediate: true })

const handleSubmit = () => {
  emit('submit', { ...formData })
}
</script>

<style scoped>
.program-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
}

.form-field--checkbox {
  flex-direction: row;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
}

.checkbox-input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
